import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShipmentHistorySection = ({ shipments, onReorder, onViewDetails }) => {
  const [expandedShipment, setExpandedShipment] = useState(null);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'text-success bg-success/10';
      case 'in transit':
        return 'text-primary bg-primary/10';
      case 'out for delivery':
        return 'text-warning bg-warning/10';
      case 'pending':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'CheckCircle';
      case 'in transit':
        return 'Truck';
      case 'out for delivery':
        return 'MapPin';
      case 'pending':
        return 'Clock';
      default:
        return 'Package';
    }
  };

  const toggleShipmentExpansion = (shipmentId) => {
    setExpandedShipment(expandedShipment === shipmentId ? null : shipmentId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Package" size={24} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground">Shipment History</h2>
          <p className="text-sm font-body text-muted-foreground">
            {shipments?.length} shipment{shipments?.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {shipments?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Package" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-body font-medium text-foreground mb-2">No shipments found</h3>
          <p className="text-sm font-body text-muted-foreground mb-6">
            Try adjusting your filters or create a new shipment
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {shipments?.map((shipment) => (
            <div key={shipment?.id} className="border border-border rounded-lg overflow-hidden">
              <div className="p-4 bg-background">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-base font-body font-medium text-foreground">
                      {shipment?.trackingNumber}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-body font-medium ${getStatusColor(shipment?.status)}`}>
                      <Icon name={getStatusIcon(shipment?.status)} size={12} className="inline mr-1" />
                      {shipment?.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-body text-muted-foreground hidden md:inline">
                      {formatDate(shipment?.shippingDate)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName={expandedShipment === shipment?.id ? "ChevronUp" : "ChevronDown"}
                      onClick={() => toggleShipmentExpansion(shipment?.id)}
                    />
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-start space-x-2 mb-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground mt-1" />
                      <div>
                        <p className="text-sm font-body font-medium text-foreground">{shipment?.recipient}</p>
                        <p className="text-xs font-body text-muted-foreground">{shipment?.destination}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Package" size={16} className="text-muted-foreground" />
                      <p className="text-xs font-body text-muted-foreground">{shipment?.packageContents}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={() => onReorder(shipment)}
                    >
                      Reorder
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewDetails(shipment)}
                    >
                      Track
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Shipment Details */}
              {expandedShipment === shipment?.id && (
                <div className="border-t border-border p-4 bg-card">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-body font-medium text-foreground mb-3">Shipment Details</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-body text-muted-foreground">Service Type:</span>
                          <span className="font-body font-medium text-foreground">{shipment?.serviceType}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-body text-muted-foreground">Weight:</span>
                          <span className="font-body font-medium text-foreground">{shipment?.weight}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-body text-muted-foreground">Dimensions:</span>
                          <span className="font-body font-medium text-foreground">{shipment?.dimensions}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-body text-muted-foreground">Cost:</span>
                          <span className="font-body font-medium text-foreground">${shipment?.cost?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-body font-medium text-foreground mb-3">Delivery Information</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-body text-muted-foreground">Shipped:</span>
                          <span className="font-body font-medium text-foreground">{formatDateTime(shipment?.shippingDate)}</span>
                        </div>
                        {shipment?.deliveryDate ? (
                          <div className="flex justify-between text-sm">
                            <span className="font-body text-muted-foreground">Delivered:</span>
                            <span className="font-body font-medium text-success">{formatDateTime(shipment?.deliveryDate)}</span>
                          </div>
                        ) : (
                          <div className="flex justify-between text-sm">
                            <span className="font-body text-muted-foreground">Est. Delivery:</span>
                            <span className="font-body font-medium text-foreground">{formatDateTime(shipment?.estimatedDelivery)}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <h5 className="text-sm font-body font-medium text-foreground mb-1">Origin</h5>
                        <p className="text-xs font-body text-muted-foreground">{shipment?.origin}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShipmentHistorySection;